// FILE: src/services/questionService.js
// Loads and normalizes questions from Supabase (primary) or bundled JSON (fallback).
// Also provides RPC callers for question mutations and session-building helpers.
//
// ── Source-of-truth policy ─────────────────────────────────────────────────────
//
//   Supabase is the single source of truth in production.
//
//   Set  REACT_APP_REQUIRE_SUPABASE=true  (e.g. in .env.production) to make
//   every Supabase failure a hard error instead of a silent fallback.  When
//   this flag is set and Supabase is unavailable or returns 0 rows, the app
//   surfaces a visible error and does NOT silently serve stale bundled JSON.
//
//   During development (or when the flag is absent/false), failures fall back
//   to bundled JSON with a console.warn so developers notice the problem
//   without being blocked.
//
// ── Reference IDs ─────────────────────────────────────────────────────────────
//
//   Every normalized question gets a stable `referenceId` field built directly
//   from its persisted `q.id`.  Examples from the real banks:
//
//     PMP q.id "pmp-0001"  →  referenceId "PMP-0001"
//     PMP q.id "pmp-1026"  →  referenceId "PMP-1026"
//     CPMAI q.id 1         →  referenceId "CPMAI-0001"
//     CPMAI q.id 88        →  referenceId "CPMAI-0088"
//
//   The transformation is bijective: every distinct q.id value produces a
//   distinct referenceId.  There are zero collisions by construction because
//   q.id values are already unique within each bank (verified).
//
//   Stability: the referenceId depends exclusively on q.id, not on array
//   position, filtered-pool index, or any runtime state.  Insert, delete, or
//   reorder questions freely — no referenceId ever changes.
//
// ── Session randomization ─────────────────────────────────────────────────────
//
//   Both Full Exam and Practice modes randomize question ORDER at creation time
//   using Fisher-Yates.  Answer choices within each question are intentionally
//   NOT shuffled — they are displayed in their original authored order (A, B, C…)
//   so the choice labels always read sequentially top-to-bottom.
//
//   Randomization occurs exactly once, at createSession() call time, so the
//   order stays stable for the duration of navigation within a session.
//
//   Deterministic mode (options: { deterministic: true }):
//     • Skips question-order shuffling — questions arrive in pool order.
//     • Intended for automated testing and QA.  It is NOT exposed anywhere
//       in the learner-facing UI so real exam sessions are always randomized.
//
//   Scoring integrity: correctAnswers stores choice ids (e.g. 'A', 'B'), not
//   array positions.  Shuffling choices never changes their ids, so all scoring
//   and display logic (QuestionCard, ExplanationPanel, ResultsPage) works
//   identically regardless of choice order.

import { getExamConfig } from '../config/exams';
import { supabase } from './supabaseClient';
import { trackEvent, trackTiming } from './telemetryService';

// When true, Supabase errors and empty results throw instead of falling back.
// Set REACT_APP_REQUIRE_SUPABASE=true in .env.production to enable.
const REQUIRE_SUPABASE = process.env.REACT_APP_REQUIRE_SUPABASE === 'true';

// PR 2C: the bundled question banks are NO LONGER statically imported here.
// They are code-split and loaded on demand via certBankLoader's dynamic-import
// map (the only production path to a bundled fallback bank). This keeps the
// banks out of main.js. See docs/lazy-loading-investigation.md.

const REFERENCE_ID_PREFIXES = {
  pmp:        'PMP',
  cpmai:      'CPMAI',
  'pmi-acp':  'ACP',
  'pmi-pmocp': 'PMOCP',
};

// Regex used to strip any embedded exam prefix already on a question's `id`
// field before we re-prefix it to a referenceId.  Keep in sync with the keys
// above (matching is case-insensitive and accepts either `-` or `_` separator).
const EMBEDDED_PREFIX_RE = /^(pmp|cpmai|pmiacp|pmipmocp|pmocp)[-_]/i;

// ── Reference ID helper ───────────────────────────────────────────────────────

/**
 * Derives a stable, collision-free, human-readable reference ID directly from
 * the question's persisted `id` field.
 *
 * Algorithm (no hashing — purely bijective):
 *   1. Stringify `questionId`.
 *   2. Strip any leading exam prefix already embedded in `q.id`
 *      (e.g. "pmp-" in "pmp-0001") to avoid double-prefixing.
 *   3. If the remaining body is purely numeric, zero-pad it to 4 digits.
 *      Otherwise, uppercase it as-is.
 *   4. Prepend the canonical uppercase exam prefix.
 *
 * Real-bank examples:
 *   generateReferenceId('pmp',     'pmp-0001')    → 'PMP-0001'
 *   generateReferenceId('pmp',     'pmp-1026')    → 'PMP-1026'
 *   generateReferenceId('cpmai',   1)             → 'CPMAI-0001'
 *   generateReferenceId('cpmai',   88)            → 'CPMAI-0088'
 *   generateReferenceId('pmi-acp', 'pmiacp_001')  → 'ACP-0001'
 *
 * @param {string}        examId     - 'pmp' | 'cpmai' | 'pmi-acp'
 * @param {string|number} questionId - the question's stable `id` field
 * @returns {string}
 */
export function generateReferenceId(examId, questionId) {
  // Lookup the canonical uppercase prefix; fall back to the examId itself
  // (uppercased) for unknown exams so callers still get a usable string.
  const prefix = REFERENCE_ID_PREFIXES[examId] || String(examId).toUpperCase();
  const raw = String(questionId);
  // Strip any existing exam prefix (e.g. "pmp-", "cpmai_", "pmiacp_") so it
  // isn't doubled.  See EMBEDDED_PREFIX_RE near the top of the file.
  const body = raw.replace(EMBEDDED_PREFIX_RE, '');
  // Purely numeric body → zero-pad to 4 digits for uniform visual width.
  // Non-numeric body (alphanumeric ids) → uppercase as-is.
  const formatted = /^\d+$/.test(body) ? body.padStart(4, '0') : body.toUpperCase();
  return `${prefix}-${formatted}`;
}


/**
 * Phase 3.5+ — defensive helper for the editor.
 *
 * Queries Supabase for every question id matching `<examId>-NNNN` and returns
 * the highest numeric N actually present in the table (any status, any type).
 * The editor calls this when generateNewQuestionId's optimistic guess
 * collides with a unique-key violation — the store may have been stale
 * (e.g. visual_interpretation rows excluded by an older loader cache) and
 * we want a guaranteed-correct next id without forcing a full refresh.
 *
 * Returns 0 when nothing is present (so caller can use 0+1=0001 padded).
 *
 * @param {string} examId - 'pmp' | 'cpmai'
 * @returns {Promise<number>}
 */
export async function fetchMaxQuestionIdNumber(examId) {
  const prefix = `${examId}-`;
  const { data, error } = await supabase
    .from('questions')
    .select('id')
    .eq('exam_id', examId)
    .order('id', { ascending: false })
    .limit(50);   // 50 is plenty — the highest numeric id is always near the
                  // top of a descending-by-id list of fixed-prefix ids.

  if (error) {
    throw new Error(`fetchMaxQuestionIdNumber(${examId}): ${error.message}`);
  }
  let maxNum = 0;
  for (const row of (data || [])) {
    const raw = String(row.id);
    if (raw.startsWith(prefix)) {
      const n = parseInt(raw.slice(prefix.length), 10);
      if (Number.isFinite(n) && n > maxNum) maxNum = n;
    }
  }
  return maxNum;
}

// ── Normalization (bundled JSON) ──────────────────────────────────────────────

/**
 * Normalizes a raw question object (bundled JSON) into the consistent app shape.
 * @param {object} q      - raw question from JSON
 * @param {string} examId - 'pmp' | 'cpmai'
 */
function normalizeQuestion(q, examId) {
  return {
    id: q.id,
    referenceId: generateReferenceId(examId, q.id),
    type: 'multiple_choice',
    question: q.question,
    choices: q.choices,
    correctAnswers: q.correctAnswers,
    explanation: q.explanation || '',
    confidence: q.confidence || 'medium',
    category: q.category || q.source || '',
    source: q.source ?? '',
    multiSelect: q.type === 'multiple_select' || q.ui?.multiSelect === true || q.multiSelect === true,
    versionNo: 1,
    updatedAt: null,
    status: 'active',
    reviewStatus: 'unreviewed',
    sourceBatchId: null,
    sourceGenerator: null,
    // ── Optional PMP-only taxonomy dimensions (migration 008) ─────────
    // These may be absent from bundled JSON (and always are from CPMAI).
    // Pass-through any value that is present; normalize `undefined`→null so
    // downstream consumers can always read the key.
    performanceDomain: q.performanceDomain ?? null,
    deliveryContext:   q.deliveryContext ?? null,
    // ── Secondary dimension (config-driven, e.g. PMOCP focus_area) ──────
    focusArea:         q.focusArea ?? q.focus_area ?? null,
    // CPMAI stores its five ECO domains on eco_domain; an active CPMAI question
    // cannot exist without one (migration 096), so the bundled fallback path has
    // to carry it through too or the editor sees an empty required field.
    ecoDomain:         q.ecoDomain ?? q.eco_domain ?? null,
    // ── Phase 2 experimental flag (migration 012) ───────────────────────
    // Default false so every legacy bundled question is scored as before.
    isExperimental: q.isExperimental === true,
    // Phase 3 — visual_interpretation exhibit support (migration 013).
    // Pass-through any image object the bundled JSON provides; null otherwise.
    image: (q.image && typeof q.image === 'object' && q.image.src)
      ? { src: String(q.image.src), alt: q.image.alt ? String(q.image.alt) : '' }
      : null,
  };
}

// PR 2C: loadQuestions() (the synchronous, statically-imported bundled loader)
// has been removed. Production code loads bundled banks on demand via
// certBankLoader.loadBundledCertBank() / ensureCertLoaded(). Tests that need
// bundled bank data import it statically via src/test-utils/loadBundledBank.

/**
 * normalizeBundledBank — turn a *dynamically imported* bundled question-bank
 * module into the app's normalized question shape, applying the same
 * active/non-retired rules as the learner path. Used by the lazy certification
 * bank loader (certBankLoader.js); behaviour-neutral seam (existing functions
 * are unchanged).
 *
 * The dynamic-import shape is tooling-dependent: importing JSON may resolve to
 * the array directly OR to `{ default: array }`. This safely handles both and
 * throws a clear error on anything malformed.
 *
 * @param {Array|{default:Array}} rawModuleOrArray  imported bundled bank
 * @param {string} examId
 * @returns {object[]} normalized, active (non-retired) questions
 */
export function normalizeBundledBank(rawModuleOrArray, examId) {
  const arr = Array.isArray(rawModuleOrArray)
    ? rawModuleOrArray
    : (rawModuleOrArray && Array.isArray(rawModuleOrArray.default) ? rawModuleOrArray.default : null);
  if (arr === null) {
    throw new Error(
      `[questionService] normalizeBundledBank: malformed bundled bank for "${examId}" ` +
      `(expected an array or { default: array })`
    );
  }
  return arr
    .filter((q) => q && (q.type === 'multiple_choice' || q.type === 'multiple_select' || q.type === 'visual_interpretation'))
    .filter((q) => q.status !== 'retired')
    .map((q) => normalizeQuestion(q, examId));
}

// ── Normalization (Supabase rows) ─────────────────────────────────────────────

/**
 * Normalizes a Supabase questions row into the same app shape as normalizeQuestion().
 * Handles snake_case columns and JSONB types returned by supabase-js.
 *
 * Column mapping:
 *   row.correct_answers    (text[])      → correctAnswers
 *   row.ui_config          (JSONB)       → multiSelect boolean
 *   row.reference_id       (text)        → referenceId (falls back to generated)
 *   row.version_no         (integer)     → versionNo
 *   row.updated_at         (timestamptz) → updatedAt
 *   row.status             (text)        → status
 *   row.review_status      (text)        → reviewStatus
 *   row.source_batch_id    (text)        → sourceBatchId
 *   row.source_generator   (text)        → sourceGenerator
 *   row.performance_domain (text)        → performanceDomain   (PMP only; null otherwise)
 *   row.delivery_context   (text)        → deliveryContext     (PMP only; null otherwise)
 *
 * @param {object} row    - raw Supabase question row
 * @param {string} examId - 'pmp' | 'cpmai'
 * @returns {object} normalized question
 */
export function normalizeSupabaseQuestion(row, examId) {
  return {
    id: row.id,
    referenceId: row.reference_id || generateReferenceId(examId, row.id),
    type: row.type || 'multiple_choice',
    question: row.question,
    choices: row.choices,
    correctAnswers: row.correct_answers,
    explanation: row.explanation || '',
    confidence: row.confidence || 'medium',
    category: row.category || '',
    source: row.source ?? '',
    multiSelect: row.type === 'multiple_select' || row.ui_config?.multiSelect === true,
    versionNo: row.version_no || 1,
    updatedAt: row.updated_at || null,
    status: row.status || 'active',
    reviewStatus: row.review_status || 'unreviewed',
    sourceBatchId: row.source_batch_id || null,
    sourceGenerator: row.source_generator || null,
    // Optional PMP-only taxonomy (migration 008).  Stays null for rows that
    // haven't been tagged and for non-PMP exams.
    performanceDomain: row.performance_domain ?? null,
    deliveryContext:   row.delivery_context   ?? null,
    // Secondary dimension column (e.g. PMOCP focus_area). Null for rows/cert
    // that don't use it. Surfaced as a Targeted Test filter + dashboard layer.
    focusArea:         row.focus_area         ?? null,
    // PMI-ACP canonical metadata (migrations 032 + 033). Exposed both as
    // top-level fields (back-compat with existing callers) and as a nested
    // `metadata` object (canonical shape per docs/bulk-import-pmi-acp.md).
    ecoDomain:         row.eco_domain      ?? null,
    ecoTask:           row.eco_task        ?? null,
    practiceArea:      row.practice_area   ?? null,
    knowledgeArea:     row.knowledge_area  ?? null,
    artifact:          row.artifact        ?? null,
    cognitiveLevel:    row.cognitive_level ?? null,
    difficulty:        row.difficulty      ?? null,
    scenarioBased:     row.scenario_based === true,
    examWeight:        row.exam_weight     ?? null,
    metadata: {
      ecoDomain:      row.eco_domain      ?? null,
      ecoTask:        row.eco_task        ?? null,
      practiceArea:   row.practice_area   ?? null,
      knowledgeArea:  row.knowledge_area  ?? null,
      artifact:       row.artifact        ?? null,
      cognitiveLevel: row.cognitive_level ?? null,
      difficulty:     row.difficulty      ?? null,
      scenarioBased:  row.scenario_based === true,
      examWeight:     row.exam_weight     ?? null,
    },
    // Phase 2 experimental flag (migration 012).  Default false for rows
    // that were inserted before the column existed.
    isExperimental:    row.is_experimental === true,
    // Phase 3 — visual_interpretation exhibit (migration 013).  PostgREST
    // returns the JSONB value as a JS object directly when present, or null.
    image: (row.image && typeof row.image === 'object' && row.image.src)
      ? { src: String(row.image.src), alt: row.image.alt ? String(row.image.alt) : '' }
      : null,
    // Case Study Question Framework linkage (nullable; null for all bank
    // questions). Used by admin surfaces only in PR 1.
    caseStudyId: row.case_study_id || null,
    caseStageId: row.case_stage_id || null,
    caseSequence: (row.case_sequence === 0 || row.case_sequence) ? row.case_sequence : null,
  };
}

// ── Supabase pagination helper ────────────────────────────────────────────────
//
// PostgREST (Supabase REST layer) defaults to a max of 1000 rows per request.
// Any `.select()` that could legitimately return more rows must page through
// the result set using `.range(from, to)`.  This helper does that with a
// configurable page size and a hard cap to prevent runaway loops.

/** Page size for paginated Supabase fetches (PostgREST default max). */
const SUPABASE_PAGE_SIZE = 1000;

/**
 * Hard safety cap — stops pagination after this many rows no matter what,
 * so a bug cannot spin forever.  Raise this if a bank legitimately exceeds
 * 100k rows.
 */
const SUPABASE_MAX_ROWS = 100_000;

/**
 * Runs a Supabase select in pages of 1000 rows until the table is drained
 * (or SUPABASE_MAX_ROWS is reached), then returns the concatenated rows.
 *
 * The caller provides a `buildQuery` function that returns a fresh query
 * builder on each call — this matters because Supabase query builders can
 * only be awaited once.
 *
 * @param {() => PostgrestFilterBuilder} buildQuery - factory returning a
 *   fresh, configured query builder (with filters + order already applied)
 * @param {string} label - used in warning messages for traceability
 * @returns {Promise<{ data: object[]|null, error: any }>}
 */
// Hard per-page deadline. Bounds genuinely stalled requests (no response,
// dead connection) so a hung page fetch can't wedge a loader forever. The
// observed 905s "slow query" was a browser-suspended tab finalizing its
// timer on resume — not a live hang — but live hangs deserve a ceiling too.
const PAGE_TIMEOUT_MS = 15000;

function pageTimeoutSignal() {
  try {
    return typeof AbortSignal !== 'undefined' && AbortSignal.timeout
      ? AbortSignal.timeout(PAGE_TIMEOUT_MS)
      : null;
  } catch (_) {
    return null;
  }
}

export async function fetchAllPaginated(buildQuery, label) {
  const all = [];
  for (let offset = 0; offset < SUPABASE_MAX_ROWS; offset += SUPABASE_PAGE_SIZE) {
    const upper = offset + SUPABASE_PAGE_SIZE - 1;
    let page = buildQuery().range(offset, upper);
    const signal = pageTimeoutSignal();
    if (signal && typeof page.abortSignal === 'function') page = page.abortSignal(signal);
    const { data, error } = await page;
    if (error) return { data: null, error };
    if (!data || data.length === 0) break;
    all.push(...data);
    if (data.length < SUPABASE_PAGE_SIZE) break; // last page
  }
  if (all.length >= SUPABASE_MAX_ROWS) {
    console.warn(
      `[questionService] fetchAllPaginated("${label}") hit SUPABASE_MAX_ROWS ` +
      `(${SUPABASE_MAX_ROWS}). Results may be incomplete.`
    );
  }
  return { data: all, error: null };
}

// ── Supabase async loaders ────────────────────────────────────────────────────

/**
 * Fetches only ACTIVE questions for a given exam from Supabase.
 * Used by the learner-facing exam sessions.
 *
 * Source-of-truth policy:
 *   • When REACT_APP_REQUIRE_SUPABASE=true: any failure throws so the caller
 *     can surface a visible error.  Never silently falls back.
 *   • Otherwise: logs a warning and returns null so the caller can fall back
 *     to the bundled JSON without blocking the user.
 *
 * @param {string} examId - 'pmp' | 'cpmai'
 * @returns {Promise<object[]|null>}  null = use bundled JSON fallback
 * @throws {Error}  only when REACT_APP_REQUIRE_SUPABASE=true and Supabase fails
 */
export async function loadQuestionsFromSupabase(examId) {
  try {
    // Paginate through all active questions; a single exam bank can (and now
    // does) legitimately exceed PostgREST's 1000-row default cap.
    const { data, error } = await fetchAllPaginated(
      () =>
        supabase
          .from('questions')
          .select('*')
          .eq('exam_id', examId)
          .eq('status', 'active')
          // Case-linked questions are delivered as a complete ordered unit by
          // the case-study runner (migration 071 RPCs) and must NEVER enter
          // the random practice/exam pools or the bundled fallback bank.
          .is('case_study_id', null)
          .in('type', ['multiple_choice', 'multiple_select', 'visual_interpretation'])
          .order('id', { ascending: true }),
      `loadQuestionsFromSupabase(${examId})`
    );

    if (error) {
      const msg = `[questionService] Supabase error for "${examId}": ${error.message}`;
      if (REQUIRE_SUPABASE) throw new Error(msg);
      console.warn(msg + ' — falling back to bundled JSON');
      trackEvent('failed_load', 'technical', { target: 'questions_load', exam_id: examId, code: 'supabase_error' });
      return null;
    }

    if (!data || data.length === 0) {
      const msg = `[questionService] Supabase returned 0 active questions for "${examId}". ` +
        'Run seed_questions.py to populate the database.';
      if (REQUIRE_SUPABASE) throw new Error(msg);
      console.warn(msg + ' — falling back to bundled JSON');
      return null;
    }

    console.info(`[questionService] Loaded ${data.length} questions for "${examId}" from Supabase`);
    return data.map((row) => normalizeSupabaseQuestion(row, examId));
  } catch (err) {
    if (REQUIRE_SUPABASE) throw err;
    console.warn('[questionService] Unexpected error loading from Supabase — falling back to bundled JSON:', err);
    trackEvent('failed_load', 'technical', { target: 'questions_load', code: 'exception' });
    return null;
  }
}

/**
 * Fetches ALL questions (new + active + retired) for a given exam from Supabase.
 * Callers: admin/review tooling (dashboard, editor, case modals) AND the
 * learner-facing certBankLoader (which filters to non-retired client-side and
 * passes its own timingLabel so learner and admin loads are separable in the
 * Slow-queries drill-down).
 *
 * Returns null on failure (caller should handle gracefully).
 *
 * @param {string} examId - 'pmp' | 'cpmai'
 * @param {object} [opts]
 * @param {string} [opts.timingLabel='questions_load_all'] telemetry target for
 *   trackTiming/failed_load — 'questions_load_bank' for the learner path.
 * @returns {Promise<object[]|null>}
 */
export async function loadAllQuestionsFromSupabase(examId, { timingLabel = 'questions_load_all' } = {}) {
  const _t0 = Date.now();
  try {
    // Paginate through the full bank; see fetchAllPaginated for rationale.
    // Secondary order by id to keep page boundaries stable when multiple
    // rows share the same updated_at timestamp.
    const { data, error } = await fetchAllPaginated(
      () =>
        supabase
          .from('questions')
          .select('*')
          .eq('exam_id', examId)
          // Admin bank: includes the interactive case types so the Question
          // Editor can edit them (PR B). Random-session leakage is prevented
          // at pool level in createSession, not here.
          .in('type', ['multiple_choice', 'multiple_select', 'visual_interpretation',
                       'enhanced_matching', 'ordered_sequence'])
          .order('updated_at', { ascending: false })
          .order('id', { ascending: true }),
      `loadAllQuestionsFromSupabase(${examId})`
    );

    // Technical health: emit 'slow_query' only if this read crossed the threshold.
    trackTiming(timingLabel, Date.now() - _t0, { exam_id: examId });

    if (error) {
      console.warn(`[questionService] loadAllQuestionsFromSupabase error for "${examId}": ${error.message}`);
      trackEvent('failed_load', 'technical', { target: timingLabel, exam_id: examId, code: 'supabase_error' });
      return null;
    }

    return (data || []).map((row) => normalizeSupabaseQuestion(row, examId));
  } catch (err) {
    console.warn('[questionService] Unexpected error in loadAllQuestionsFromSupabase:', err);
    trackEvent('failed_load', 'technical', { target: timingLabel, exam_id: examId, code: 'exception' });
    return null;
  }
}

// ── Metadata-pool projection (session-draw stage 3, PR 1) ────────────────────
//
// The candidate-pool draw needs only the fields the selection engine reads
// (createSession / selectWeighted / filterByMetadata / buildFullExamWithCases
// pool filtering) — not question content. Projecting these columns cuts the
// learner draw payload from multiple MB to a few hundred KB per cert.
//
// PARITY CONTRACT (deterministic fixtures depend on this):
//   • Same `.in('type', …)` filter as loadAllQuestionsFromSupabase.
//   • Same ORDER BY: updated_at DESC, id ASC. deterministic:true selection
//     consumes pool order directly, so a different ORDER BY changes draws.
//   • Rows are normalized through the same normalizeSupabaseQuestion().
//
// `difficulty` is projected for forward compatibility even though no current
// selection path consumes it. `subcategory` and `tags` are intentionally NOT
// here: they are bundle-only filters today — normalizeSupabaseQuestion never
// maps them from Supabase rows, so they can only surface from the bundled
// JSON fallback banks (see METADATA_FIELDS below).
export const QUESTION_POOL_COLUMNS =
  'id, exam_id, type, status, category, performance_domain, delivery_context, '
  + 'practice_area, focus_area, difficulty, is_experimental, case_study_id, updated_at';

/**
 * Fetches the lightweight metadata pool for a cert (selection fields only,
 * no question content). Same row set, filters, and ordering as
 * loadAllQuestionsFromSupabase — only the column projection differs.
 *
 * Telemetry: emits timing under target `questions_draw_pool` (with row_count
 * and approx_bytes) — never `questions_load_bank` / `questions_load_all`,
 * which remain the legacy learner path and intentional admin loads.
 *
 * Returns null on failure (caller should handle gracefully).
 *
 * @param {string} examId
 * @returns {Promise<object[]|null>} normalized metadata-only questions
 */
// ── Admin dashboard summary projection (migration 099) ──────────────────
//
// The Question Bank Dashboard aggregates the WHOLE bank across every cert, but
// reads only ids, labels, a 90-character stem preview, and whether an
// explanation exists. Loading it through loadAllQuestionsFromSupabase meant
// ~18.3 MB of question stems, choices and explanations per refresh (measured in
// prod 2026-09-14), which is why questions_load_all averaged 11-16 s per exam.
//
// `questions_admin_summary` (migration 099) is a view over the same rows, with
// the same type filter, exposing question_preview + has_explanation instead of
// the heavy text columns. ~2.2 MB total.
//
// PARITY CONTRACT: the view's type filter mirrors the `.in('type', ...)` used by
// loadAllQuestionsFromSupabase and QUESTION_POOL_COLUMNS, so bank counts agree
// across all three paths. If that filter ever changes, change it in all three.
export const QUESTION_SUMMARY_COLUMNS =
  'id, exam_id, reference_id, type, status, review_status, category, '
  + 'performance_domain, delivery_context, focus_area, eco_domain, practice_area, '
  + 'case_study_id, updated_at, question_preview, has_explanation';

/**
 * Maps a questions_admin_summary row to the camelCase shape the dashboard reads.
 *
 * `question` carries the 160-char preview (the dashboard truncates to 90 before
 * rendering) and `hasExplanation` replaces the full explanation text. Bundled
 * JSON fallback banks still carry the real fields, so consumers must tolerate
 * both shapes — see isMissingExplanation() in dashboardService.
 *
 * @param {object} row
 * @param {string} examId
 * @returns {object}
 */
export function normalizeQuestionSummary(row, examId) {
  return {
    id:                String(row.id),
    examId:            row.exam_id || examId,
    referenceId:       row.reference_id || null,
    type:              row.type || 'multiple_choice',
    status:            row.status || 'active',
    reviewStatus:      row.review_status || 'unreviewed',
    category:          row.category || '',
    performanceDomain: row.performance_domain || '',
    deliveryContext:   row.delivery_context || '',
    focusArea:         row.focus_area || '',
    ecoDomain:         row.eco_domain || '',
    practiceArea:      row.practice_area || '',
    caseStudyId:       row.case_study_id || null,
    updatedAt:         row.updated_at || null,
    question:          row.question_preview || '',
    hasExplanation:    row.has_explanation === true,
  };
}

/**
 * Fetches the admin dashboard summary projection for a cert.
 *
 * Same row set as loadAllQuestionsFromSupabase, ~8x smaller on the wire.
 * Returns null on failure so the caller can fall back to the bundled bank,
 * exactly as the full loader does.
 *
 * Telemetry: timings land under target `questions_dashboard_summary`, kept
 * separate from `questions_load_all` / `questions_load_bank` /
 * `questions_draw_pool` so the Slow-queries drill-down can tell the admin
 * dashboard apart from learner traffic.
 *
 * @param {string} examId
 * @returns {Promise<object[]|null>}
 */
export async function loadQuestionSummariesFromSupabase(examId) {
  const _t0 = Date.now();
  try {
    const { data, error } = await fetchAllPaginated(
      () =>
        supabase
          .from('questions_admin_summary')
          .select(QUESTION_SUMMARY_COLUMNS)
          .eq('exam_id', examId)
          .order('updated_at', { ascending: false })
          .order('id', { ascending: true }),
      `loadQuestionSummariesFromSupabase(${examId})`
    );

    const rowCount = Array.isArray(data) ? data.length : 0;
    let approxBytes = null;
    try { approxBytes = data ? JSON.stringify(data).length : null; } catch (_) { /* non-serializable — skip */ }
    trackTiming('questions_dashboard_summary', Date.now() - _t0, {
      exam_id: examId, row_count: rowCount, approx_bytes: approxBytes,
    });

    if (error) {
      console.warn(`[questionService] loadQuestionSummariesFromSupabase error for "${examId}": ${error.message}`);
      trackEvent('failed_load', 'technical', { target: 'questions_dashboard_summary', exam_id: examId, code: 'supabase_error' });
      return null;
    }

    return (data || []).map((row) => normalizeQuestionSummary(row, examId));
  } catch (err) {
    console.warn('[questionService] Unexpected error in loadQuestionSummariesFromSupabase:', err);
    trackEvent('failed_load', 'technical', { target: 'questions_dashboard_summary', exam_id: examId, code: 'exception' });
    return null;
  }
}

export async function loadQuestionPoolFromSupabase(examId) {
  const _t0 = Date.now();
  try {
    const { data, error } = await fetchAllPaginated(
      () =>
        supabase
          .from('questions')
          .select(QUESTION_POOL_COLUMNS)
          .eq('exam_id', examId)
          .in('type', ['multiple_choice', 'multiple_select', 'visual_interpretation',
                       'enhanced_matching', 'ordered_sequence'])
          .order('updated_at', { ascending: false })
          .order('id', { ascending: true }),
      `loadQuestionPoolFromSupabase(${examId})`
    );

    const rowCount = Array.isArray(data) ? data.length : 0;
    let approxBytes = null;
    try { approxBytes = data ? JSON.stringify(data).length : null; } catch (_) { /* non-serializable — skip */ }
    trackTiming('questions_draw_pool', Date.now() - _t0, {
      exam_id: examId, row_count: rowCount, approx_bytes: approxBytes,
    });

    if (error) {
      console.warn(`[questionService] loadQuestionPoolFromSupabase error for "${examId}": ${error.message}`);
      trackEvent('failed_load', 'technical', { target: 'questions_draw_pool', exam_id: examId, code: 'supabase_error' });
      return null;
    }

    return (data || []).map((row) => normalizeSupabaseQuestion(row, examId));
  } catch (err) {
    console.warn('[questionService] Unexpected error in loadQuestionPoolFromSupabase:', err);
    trackEvent('failed_load', 'technical', { target: 'questions_draw_pool', exam_id: examId, code: 'exception' });
    return null;
  }
}

/**
 * Fetches full question rows for an EXPLICIT id list (the hydration half of
 * the pool-metadata + content-by-ID session draw). Chunked `.in('id', …)`
 * reads keep URLs bounded; callers re-order results to the drawn sequence,
 * so no ORDER BY is needed here.
 *
 * Telemetry: emits timing under `questions_fetch_session` with requested vs
 * returned row counts + approx_bytes. Failures emit `failed_load` under the
 * same target and return null (caller decides retry semantics).
 *
 * @param {string}   examId
 * @param {string[]} ids - question primary keys (already String()-normalized)
 * @returns {Promise<object[]|null>} normalized full questions, or null
 */
// Every column normalizeSupabaseQuestion consumes EXCEPT `explanation` — the
// projection used when explanation deferral is on (stage 3, PR 3). If
// normalizeSupabaseQuestion ever starts reading a new column, add it here too;
// the "normalize parity" test in questionService.pool.test.js locks the two
// together.
export const QUESTION_CONTENT_COLUMNS_SANS_EXPLANATION =
  'id, exam_id, reference_id, type, question, choices, correct_answers, '
  + 'confidence, category, source, ui_config, version_no, updated_at, status, '
  + 'review_status, source_batch_id, source_generator, performance_domain, '
  + 'delivery_context, focus_area, eco_domain, eco_task, practice_area, '
  + 'knowledge_area, artifact, cognitive_level, difficulty, scenario_based, '
  + 'exam_weight, is_experimental, image, case_study_id, case_stage_id, case_sequence';

export async function loadQuestionsByIds(examId, ids, { excludeExplanation = false } = {}) {
  if (!Array.isArray(ids) || ids.length === 0) return [];
  const _t0 = Date.now();
  const CHUNK = 200; // PostgREST in() via querystring — keep URLs well under limits
  try {
    const all = [];
    for (let i = 0; i < ids.length; i += CHUNK) {
      const { data, error } = await supabase
        .from('questions')
        .select(excludeExplanation ? QUESTION_CONTENT_COLUMNS_SANS_EXPLANATION : '*')
        .eq('exam_id', examId)
        .in('id', ids.slice(i, i + CHUNK));
      if (error) {
        console.warn(`[questionService] loadQuestionsByIds error for "${examId}": ${error.message}`);
        trackEvent('failed_load', 'technical', { target: 'questions_fetch_session', exam_id: examId, code: 'supabase_error' });
        return null;
      }
      all.push(...(data || []));
    }
    let approxBytes = null;
    try { approxBytes = JSON.stringify(all).length; } catch (_) { /* skip */ }
    trackTiming('questions_fetch_session', Date.now() - _t0, {
      exam_id: examId, requested: ids.length, row_count: all.length, approx_bytes: approxBytes,
      explanations_deferred: excludeExplanation || undefined,
    });
    const normalized = all.map((row) => normalizeSupabaseQuestion(row, examId));
    if (!excludeExplanation) return normalized;
    // Deferred: make the absence DETECTABLE. normalizeSupabaseQuestion maps a
    // missing column to '' — indistinguishable from a genuinely empty
    // explanation — so mark the rows and unset the field.
    return normalized.map((q) => ({ ...q, explanation: undefined, explanationDeferred: true }));
  } catch (err) {
    console.warn('[questionService] Unexpected error in loadQuestionsByIds:', err);
    trackEvent('failed_load', 'technical', { target: 'questions_fetch_session', exam_id: examId, code: 'exception' });
    return null;
  }
}

/**
 * Fetches ONLY {id, explanation} for an explicit id list — the on-demand half
 * of explanation deferral (stage 3, PR 3). Called per question at Practice
 * submit and in one batch at session end for results/review.
 *
 * Telemetry: emits timing under `questions_load_review` with requested vs
 * returned counts + approx_bytes; failures emit `failed_load` under the same
 * target and return null (callers keep the deferred marker so a later pass
 * can retry).
 *
 * @param {string}   examId
 * @param {string[]} ids
 * @returns {Promise<Array<{id:any, explanation:string}>|null>}
 */
export async function loadExplanationsByIds(examId, ids) {
  if (!Array.isArray(ids) || ids.length === 0) return [];
  const _t0 = Date.now();
  const CHUNK = 200;
  try {
    const all = [];
    for (let i = 0; i < ids.length; i += CHUNK) {
      const { data, error } = await supabase
        .from('questions')
        .select('id, explanation')
        .eq('exam_id', examId)
        .in('id', ids.slice(i, i + CHUNK));
      if (error) {
        console.warn(`[questionService] loadExplanationsByIds error for "${examId}": ${error.message}`);
        trackEvent('failed_load', 'technical', { target: 'questions_load_review', exam_id: examId, code: 'supabase_error' });
        return null;
      }
      all.push(...(data || []));
    }
    let approxBytes = null;
    try { approxBytes = JSON.stringify(all).length; } catch (_) { /* skip */ }
    trackTiming('questions_load_review', Date.now() - _t0, {
      exam_id: examId, requested: ids.length, row_count: all.length, approx_bytes: approxBytes,
    });
    return all.map((row) => ({ id: row.id, explanation: row.explanation || '' }));
  } catch (err) {
    console.warn('[questionService] Unexpected error in loadExplanationsByIds:', err);
    trackEvent('failed_load', 'technical', { target: 'questions_load_review', exam_id: examId, code: 'exception' });
    return null;
  }
}

/**
 * Single-question lookup by primary key. Used by deep-link paths (e.g.
 * `?preview=<id>`) that previously fetched the entire exam bank just to
 * .find(q => q.id === pid). Returns a normalized question or null.
 *
 * Emits a `slow_query` telemetry event under target `questions_load_by_id`
 * if the read exceeds the configured threshold — kept separate from
 * `questions_load_all` so the two paths can be observed independently in
 * the Slow queries drill-down.
 *
 * @param {string} id     - question id (primary key)
 * @param {string} examId - 'pmp' | 'cpmai' | etc., used only for normalization
 * @returns {Promise<object|null>}
 */
export async function loadQuestionById(id, examId) {
  if (!id) return null;
  const _t0 = Date.now();
  try {
    const { data, error } = await supabase
      .from('questions')
      .select('*')
      .eq('id', id)
      .maybeSingle();

    trackTiming('questions_load_by_id', Date.now() - _t0, { exam_id: examId || null });

    if (error) {
      console.warn(`[questionService] loadQuestionById error for "${id}": ${error.message}`);
      trackEvent('failed_load', 'technical', { target: 'questions_load_by_id', exam_id: examId || null, code: 'supabase_error' });
      return null;
    }
    if (!data) return null;
    return normalizeSupabaseQuestion(data, examId || data.exam_id);
  } catch (err) {
    console.warn('[questionService] Unexpected error in loadQuestionById:', err);
    trackEvent('failed_load', 'technical', { target: 'questions_load_by_id', exam_id: examId || null, code: 'exception' });
    return null;
  }
}

// ── Question mutation RPCs ────────────────────────────────────────────────────
//
// All mutating operations go through Postgres RPC functions defined in
// supabase/migrations/002_question_history.sql and 004_lifecycle_fields.sql.
// Each RPC:
//   • Runs in a single transaction
//   • Writes a history row before returning
//   • Returns the complete updated questions row as JSONB
//
// The caller normalizes the returned row with normalizeSupabaseQuestion()
// and refreshes local state — guaranteeing the UI reflects DB reality.

/**
 * Atomically updates a question and records an 'update' history entry.
 *
 * @param {object} params
 * @param {string}   params.id
 * @param {string}   params.examId
 * @param {string}   params.question
 * @param {Array}    params.choices           - [{id, text}, …]
 * @param {string[]} params.correctAnswers
 * @param {string}   params.explanation
 * @param {string}   params.category
 * @param {string}   params.confidence        - 'low'|'medium'|'high'
 * @param {boolean}  params.multiSelect
 * @param {string}   [params.changeNote]
 * @param {string}   [params.performanceDomain] - PMP only: 'People'|'Process'|'Business'|null
 * @param {string}   [params.deliveryContext]   - PMP only: 'Predictive'|'Agile'|'Hybrid'|'AI'|null
 * @returns {Promise<object>} raw Supabase row (pass through normalizeSupabaseQuestion)
 * @throws {Error} on any Supabase failure
 */
export async function updateQuestionWithHistory({
  id, examId, question, choices, correctAnswers,
  explanation, category, confidence, multiSelect,
  // Phase 3 — optional explicit type override (multiple_choice|multiple_select|visual_interpretation).
  // When omitted, the type is derived from multiSelect (legacy callers).
  type = null,
  changeNote = '',
  performanceDomain = null,
  deliveryContext   = null,
  // Phase 2 (migration 012): null = preserve existing value, true/false = set.
  isExperimental    = null,
  // Phase 3 — optional exhibit { src, alt }; null preserves existing on update.
  image          = null,
  // Phase 4 — caller identity stamped into question_history.changed_by.
  changedBy = null,
  // PMI-ACP ECO metadata (migration 032). NULL = preserve existing value.
  ecoDomain        = null,
  ecoTask          = null,
  practiceArea     = null,
  knowledgeArea    = null,
  artifact         = null,
  cognitiveLevel   = null,
  difficulty       = null,
  // PMI-ACP canonical metadata (migration 033).
  scenarioBased    = null,
  examWeight       = null,
  // Source / Reference (migration 040). null = preserve existing on update.
  source           = null,
  // Secondary dimension (migration 073). null preserves the stored value.
  focusArea        = null,
}) {
  const { data, error } = await supabase.rpc('update_question_with_history', {
    p_id:                 String(id),
    p_question:           question,
    p_choices:            choices,
    p_correct_answers:    correctAnswers,
    p_explanation:        explanation || '',
    p_category:           category || '',
    p_confidence:         confidence || 'medium',
    p_ui_config:          { multiSelect: multiSelect === true },
    p_type:               type || (multiSelect ? 'multiple_select' : 'multiple_choice'),
    p_change_note:        changeNote,
    // PMP-only taxonomy (migration 008).  Safe to always send — the DB CHECK
    // constraint allows NULL and enum values; non-PMP callers pass null.
    p_performance_domain: performanceDomain || null,
    p_delivery_context:   deliveryContext   || null,
    // Phase 2 (migration 012): pass through to the COALESCE in the RPC.
    // null preserves the existing stored value; true/false explicitly sets it.
    p_is_experimental:    isExperimental === true ? true : (isExperimental === false ? false : null),
    // Phase 3 — pass through image when caller provides it.  COALESCE on the
    // server side preserves the existing value when null, mirroring the
    // taxonomy + isExperimental pattern.  Caller passes a JS object {src,alt}.
    p_image:              image && image.src ? { src: image.src, alt: image.alt || '' } : null,
    p_changed_by:         changedBy || null,
    // PMI-ACP ECO metadata (migration 032).
    p_eco_domain:         ecoDomain      || null,
    p_eco_task:           ecoTask        || null,
    p_practice_area:      practiceArea   || null,
    p_knowledge_area:     knowledgeArea  || null,
    p_artifact:           artifact       || null,
    p_cognitive_level:    cognitiveLevel || null,
    p_difficulty:         difficulty     || null,
    // PMI-ACP canonical metadata (migration 033).
    p_scenario_based:     scenarioBased,
    p_exam_weight:        examWeight     || null,
    // Source / Reference (migration 040). '' clears it; null preserves.
    p_focus_area:         focusArea ?? null,
    p_source:             source ?? null,
  });

  if (error) throw new Error(`Save failed: ${error.message}`);
  return data; // raw JSONB row
}

/**
 * Atomically inserts a new question and records a 'create' history entry.
 *
 * @param {object} params
 * @param {string}   params.id
 * @param {string}   params.examId
 * @param {string}   params.referenceId
 * @param {string}   params.question
 * @param {Array}    params.choices
 * @param {string[]} params.correctAnswers
 * @param {string}   params.explanation
 * @param {string}   params.category
 * @param {string}   params.confidence
 * @param {boolean}  params.multiSelect
 * @param {string}   [params.changeNote]
 * @param {string}   [params.status]          - 'new' (default) | 'active'
 * @param {string}   [params.reviewStatus]    - 'unreviewed' (default)
 * @param {string}   [params.sourceBatchId]
 * @param {string}   [params.sourceGenerator]
 * @returns {Promise<object>} raw Supabase row
 * @throws {Error} on any Supabase failure
 */
export async function createQuestionWithHistory({
  id, examId, referenceId, question, choices, correctAnswers,
  explanation, category, confidence, multiSelect,
  // Phase 3 — optional explicit type override (multiple_choice|multiple_select|visual_interpretation).
  type = null,
  changeNote = '',
  status = 'new',
  reviewStatus = 'unreviewed',
  sourceBatchId = null,
  sourceGenerator = null,
  performanceDomain = null,
  deliveryContext   = null,
  // Phase 2 (migration 012): null = preserve existing value, true/false = set.
  isExperimental    = null,
  // Phase 3 -- optional exhibit { src, alt }; null on plain text questions.
  image          = null,
  // Phase 4 -- caller identity stamped into question_history.changed_by.
  changedBy = null,
  // PMI-ACP ECO metadata (migration 032). Null on create = NULL in DB.
  ecoDomain        = null,
  ecoTask          = null,
  practiceArea     = null,
  knowledgeArea    = null,
  artifact         = null,
  cognitiveLevel   = null,
  difficulty       = null,
  // PMI-ACP canonical metadata (migration 033).
  scenarioBased    = false,
  examWeight       = null,
  // Source / Reference (migration 040).
  source           = null,
  // Secondary dimension (migration 042 column, RPC param via migration 073).
  // Config-driven per cert (exams.js secondaryDimension) — PMOCP focus_area.
  focusArea        = null,
}) {
  const { data, error } = await supabase.rpc('create_question_with_history', {
    p_id:                 String(id),
    p_exam_id:            examId,
    p_reference_id:       referenceId,
    p_type:               type || (multiSelect ? 'multiple_select' : 'multiple_choice'),
    p_question:           question,
    p_choices:            choices,
    p_correct_answers:    correctAnswers,
    p_explanation:        explanation || '',
    p_category:           category || '',
    p_confidence:         confidence || 'medium',
    p_ui_config:          { multiSelect: multiSelect === true },
    p_change_note:        changeNote,
    p_status:             status,
    p_review_status:      reviewStatus,
    p_source_batch_id:    sourceBatchId || null,
    p_source_generator:   sourceGenerator || null,
    // Optional PMP-only taxonomy (migration 008).
    p_performance_domain: performanceDomain || null,
    p_delivery_context:   deliveryContext   || null,
    // Phase 2 (migration 012): default false on create.  Caller may pass true
    // to mark a freshly-created question as experimental from the start.
    p_is_experimental:    isExperimental === true,
    p_image:              image && image.src ? { src: image.src, alt: image.alt || '' } : null,
    p_changed_by:         changedBy || null,
    // PMI-ACP ECO metadata (migration 032).
    p_eco_domain:         ecoDomain      || null,
    p_eco_task:           ecoTask        || null,
    p_practice_area:      practiceArea   || null,
    p_knowledge_area:     knowledgeArea  || null,
    p_artifact:           artifact       || null,
    p_cognitive_level:    cognitiveLevel || null,
    p_difficulty:         difficulty     || null,
    // PMI-ACP canonical metadata (migration 033).
    p_scenario_based:     scenarioBased === true,
    p_exam_weight:        examWeight     || null,
    // Source / Reference (migration 040).
    p_focus_area:        focusArea || null,
    p_source:             source ?? null,
  });

  if (error) throw new Error(`Create failed: ${error.message}`);
  return data;
}

/**
 * Retires a question (sets status='retired') and records a 'retire' history entry.
 * Retired questions are excluded from loadQuestionsFromSupabase (status filter).
 *
 * @param {string} id
 * @param {string} [changeNote]
 * @returns {Promise<object>} raw updated row
 * @throws {Error} on any Supabase failure
 */
export async function retireQuestion(id, changeNote = 'Question retired', changedBy = null) {
  const { data, error } = await supabase.rpc('retire_question', {
    p_id:          String(id),
    p_change_note: changeNote,
    p_changed_by:  changedBy || null,
  });

  if (error) throw new Error(`Retire failed: ${error.message}`);
  return data;
}

/**
 * Restores a question to a specific historical version and records a 'restore'
 * history entry.  The restored state is the `new_data` snapshot from that
 * history row.  Always sets status='active' so retired questions can be revived.
 *
 * @param {string} questionId
 * @param {string} historyId   - UUID of the question_history row to restore from
 * @param {string} [changeNote]
 * @returns {Promise<object>} raw updated row
 * @throws {Error} on any Supabase failure
 */
export async function restoreQuestionVersion(questionId, historyId, changeNote = 'Restored from history', changedBy = null) {
  const { data, error } = await supabase.rpc('restore_question_version', {
    p_question_id: String(questionId),
    p_history_id:  historyId,
    p_change_note: changeNote,
    p_changed_by:  changedBy || null,
  });

  if (error) throw new Error(`Restore failed: ${error.message}`);
  return data;
}

/**
 * Updates a question's lifecycle fields (status and/or reviewStatus) and records
 * a 'review' history entry.  Does NOT touch content fields.
 *
 * Pass null for a param to leave that field unchanged.
 *
 * @param {object} params
 * @param {string}  params.id
 * @param {string}  [params.status]       - 'new' | 'active' | 'retired'
 * @param {string}  [params.reviewStatus] - 'unreviewed' | 'reported' | 'in_review' | 'reviewed'
 * @param {string}  [params.changeNote]
 * @returns {Promise<object>} raw updated row
 * @throws {Error} on any Supabase failure
 */
export async function updateQuestionLifecycle({
  id,
  status = null,
  reviewStatus = null,
  changeNote = '',
  // Phase 4 — caller identity stamped into question_history.changed_by.
  changedBy = null,
}) {
  const { data, error } = await supabase.rpc('update_question_lifecycle', {
    p_id:            String(id),
    p_status:        status || null,
    p_review_status: reviewStatus || null,
    p_change_note:   changeNote,
    p_changed_by:    changedBy || null,
  });

  if (error) throw new Error(`Lifecycle update failed: ${error.message}`);
  return data;
}

/**
 * Fetches the version history for a single question, most-recent first.
 * Returns only the lightweight summary columns needed for the history panel
 * (not old_data / new_data to keep the payload small).
 *
 * @param {string} questionId
 * @returns {Promise<object[]>}  array of history summary rows
 * @throws {Error} on any Supabase failure
 */
export async function fetchQuestionHistory(questionId) {
  const { data, error } = await supabase
    .from('question_history')
    .select('id, version_no, change_type, changed_at, changed_by, change_note')
    .eq('question_id', String(questionId))
    .order('version_no', { ascending: false });

  if (error) throw new Error(`History fetch failed: ${error.message}`);

  // Map snake_case DB columns → camelCase expected by the history panel UI
  return (data || []).map((row) => ({
    id:         row.id,
    versionNo:  row.version_no,
    changeType: row.change_type,
    changedAt:  row.changed_at,
    changedBy:  row.changed_by,
    changeNote: row.change_note,
  }));
}

// ── Shuffle helpers ───────────────────────────────────────────────────────────

/**
 * Returns a shuffled copy of an array using Fisher-Yates.
 * Does not mutate the original.
 */
function shuffle(array) {
  const arr = [...array];
  for (let i = arr.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [arr[i], arr[j]] = [arr[j], arr[i]];
  }
  return arr;
}

// ── Weighted category selection (Full Exam) ──────────────────────────────────

/**
 * Converts percentage-based category distribution into absolute question
 * counts that sum exactly to `total`.
 *
 * Uses largest-remainder rounding: each category gets its floored share,
 * then the remaining slots are distributed one-at-a-time to the categories
 * with the largest fractional remainders.
 *
 * @param {Object<string,number>} distribution - { category: percent, … } summing to 100
 * @param {number}                total        - target question count (e.g. 180)
 * @returns {Object<string,number>}            - { category: count, … } summing to total
 */
export function computeCategoryCounts(distribution, total) {
  const entries = Object.entries(distribution);
  const exact = entries.map(([cat, pct]) => ({ cat, exact: (pct / 100) * total }));

  // Floor each value and track the fractional remainder
  let floored = exact.map((e) => ({
    cat: e.cat,
    count: Math.floor(e.exact),
    remainder: e.exact - Math.floor(e.exact),
  }));

  // Distribute leftover slots to the categories with the largest remainders
  let assigned = floored.reduce((sum, e) => sum + e.count, 0);
  let gap = total - assigned;

  // Sort by remainder descending (stable secondary sort by category name)
  const sorted = [...floored].sort(
    (a, b) => b.remainder - a.remainder || a.cat.localeCompare(b.cat)
  );
  for (let i = 0; i < gap; i++) {
    sorted[i].count += 1;
  }

  const result = {};
  for (const e of floored) result[e.cat] = e.count;
  return result;
}

/**
 * Selects questions from a pool using weighted category distribution.
 *
 * 1. Group the pool by category.
 * 2. Compute target counts via computeCategoryCounts.
 * 3. Shuffle each category bucket and take up to the target count.
 * 4. If a category has fewer questions than its target, the shortfall
 *    is redistributed to categories with the most surplus (those with
 *    the biggest gap between available and already-taken).
 * 5. The final selection is shuffled together so categories are interleaved.
 *
 * @param {object[]}              pool          - filtered active question pool
 * @param {Object<string,number>} distribution  - { category: percent, … }
 * @param {number}                total         - target session length
 * @param {boolean}               deterministic - skip final shuffle if true
 * @returns {object[]} selected questions
 */
export function selectWeighted(pool, distribution, total, deterministic = false, field = 'category') {
  // `field` is the normalized question property the distribution keys refer
  // to. 'category' (methodology phase / PMI domain) is the historical default;
  // CPMAI passes 'ecoDomain' so its Full Exam is drawn on PMI's ECO weights.
  const bucketOf = (q) => (q && q[field]) || '';

  // Guard: if no pool questions match any distribution category, fall back to
  // simple shuffle-and-slice.  This avoids degenerate behaviour when the pool
  // consists entirely of uncategorized or fixture questions.
  const distCategories = new Set(Object.keys(distribution));
  const matchCount = pool.filter((q) => distCategories.has(bucketOf(q))).length;
  if (matchCount === 0) {
    const ordered = deterministic ? [...pool] : shuffle(pool);
    return ordered.slice(0, total);
  }

  // Group pool by category
  const buckets = {};
  for (const q of pool) {
    const cat = bucketOf(q);
    if (!buckets[cat]) buckets[cat] = [];
    buckets[cat].push(q);
  }

  // Shuffle each bucket (unless deterministic) so we draw randomly from each
  if (!deterministic) {
    for (const cat of Object.keys(buckets)) {
      buckets[cat] = shuffle(buckets[cat]);
    }
  }

  const targets = computeCategoryCounts(distribution, total);
  const selected = {};   // cat → question[]
  let totalShortfall = 0;

  // First pass: take up to target from each category
  for (const [cat, target] of Object.entries(targets)) {
    const available = buckets[cat] || [];
    const take = Math.min(target, available.length);
    selected[cat] = available.slice(0, take);
    if (take < target) {
      totalShortfall += target - take;
    }
  }

  // Second pass: fill shortfall from surplus categories
  if (totalShortfall > 0) {
    // Build a list of surplus categories sorted by remaining available (descending).
    // Include both distribution categories with leftover questions AND any
    // non-distribution categories in the pool (e.g. "Ethics" questions).
    const allCats = new Set([...Object.keys(targets), ...Object.keys(buckets)]);
    const surplus = [...allCats]
      .map((cat) => {
        const available = (buckets[cat] || []).length;
        const taken = (selected[cat] || []).length;
        return { cat, remaining: available - taken };
      })
      .filter((s) => s.remaining > 0)
      .sort((a, b) => b.remaining - a.remaining);

    let still = totalShortfall;
    // Round-robin through surplus categories to distribute evenly
    while (still > 0 && surplus.some((s) => s.remaining > 0)) {
      for (const s of surplus) {
        if (still <= 0) break;
        if (s.remaining <= 0) continue;
        const bucket = buckets[s.cat] || [];
        if (!selected[s.cat]) selected[s.cat] = [];
        const nextIdx = selected[s.cat].length;
        if (nextIdx < bucket.length) {
          selected[s.cat].push(bucket[nextIdx]);
          s.remaining -= 1;
          still -= 1;
        } else {
          s.remaining = 0;
        }
      }
    }
  }

  // Flatten all selected questions and shuffle the final order
  const flat = Object.values(selected).flat();
  return deterministic ? flat : shuffle(flat);
}

/**
 * Full Exam stratification for an exam config: `{ field, percentages }` or
 * null. Prefers `fullExamDistribution` (field defaults to 'category'), falls
 * back to the historical `categoryDistribution` on 'category'. Same rule as
 * exams.getFullExamDistribution — kept local so a mocked config still resolves.
 */
export function resolveFullExamStratification(config) {
  if (!config) return null;
  const fx = config.fullExamDistribution;
  if (fx && fx.percentages) return { field: fx.field || 'category', percentages: fx.percentages };
  if (config.categoryDistribution) return { field: 'category', percentages: config.categoryDistribution };
  return null;
}

// ── createSession ─────────────────────────────────────────────────────────────

/**
 * Builds a session question list based on exam config and mode.
 *
 * Only `active` questions are included in learner-facing sessions.
 * Questions with status 'new' or 'retired' are always excluded.
 *
 * Default (learner-facing) behaviour:
 *   Both Full Exam and Practice modes shuffle question ORDER only.
 *   Choices within each question are always displayed in their original
 *   authored order (A, B, C…) so labels read sequentially top-to-bottom.
 *   Randomization happens exactly once at call time and is stable for the
 *   life of the session.
 *
 * Deterministic mode (options: { deterministic: true }):
 *   Skips question-order shuffling.  Questions arrive in pool order.
 *   Use this for automated tests and QA only — it is not accessible from
 *   any learner-facing UI.
 *
 * Full Exam (with categoryDistribution):
 *   - Selects questions using weighted category distribution matching the
 *     official exam blueprint (e.g. PMI for PMP, AAAI for CPMAI).
 *   - If a category lacks enough questions, the shortfall is filled from
 *     categories with the most surplus.
 *   - Final selection is shuffled so categories are interleaved.
 *
 * Full Exam (without categoryDistribution):
 *   - Falls back to shuffling the full pool and taking totalQuestions.
 *
 * Practice Exam:
 *   - Shuffles the pool (unless deterministic), then takes up to
 *     config.practiceExam.defaultQuestions questions.
 *   - If pool is smaller than the limit, uses all available.
 *
 * @param {string}        examId          - "pmp" | "cpmai"
 * @param {string}        mode            - "full" | "practice" | "targeted" | "targetedTest"
 * @param {object[]|null} [pool]          - REQUIRED question pool (PR 2C). Pass the
 *                                          lazily-loaded bank / shared in-memory store
 *                                          so editor edits and bulk imports are
 *                                          reflected in new exam sessions. A missing
 *                                          pool yields an empty session (no internal
 *                                          synchronous load).
 * @param {object}        [options]       - session options
 * @param {boolean}       [options.deterministic=false]
 * @param {Set<string>|string[]} [options.excludeIds=[]]
 * @param {string[]}      [options.phases=[]]
 *   Targeted mode only.  Array of category strings to filter by,
 *   e.g. ['Data Understanding', 'Model Development'].
 *   If the filtered pool is smaller than the configured limit, all
 *   matching questions are used rather than truncating further.
 * @returns {object[]} session question array
 */
// ── Experimental question selection (Phase 2, migration 012) ─────────────────
//
// `applyExperimentalSelection(scoredPool, expPool, totalLimit, expConfig, deterministic)`
//
// Draws `count` questions from the experimental pool and `totalLimit - count`
// from the scored pool, then shuffles the combined set into a single ordered
// list of length exactly `totalLimit` (or fewer if both pools combined are
// smaller than the limit).
//
// If the experimental pool is too small to satisfy `count`, the shortfall is
// filled from the scored pool — total session length is preserved, and the
// ratio of scored:experimental gracefully degrades.  No errors are thrown for
// undersized experimental banks; learners just see slightly fewer experimental
// questions than configured.
//
// When experimental config is disabled (or `count <= 0`), behaviour is
// identical to a normal pool selection — exported as `selectScoredOnly` for
// the rare caller that wants to bypass the experimental path entirely.
function applyExperimentalSelection(scoredPool, expPool, totalLimit, expConfig, deterministic) {
  const requested = Math.max(0, expConfig?.count ?? 0);
  const cap       = Math.min(requested, expPool.length, totalLimit);

  const orderedExp    = deterministic ? expPool    : shuffle(expPool);
  const orderedScored = deterministic ? scoredPool : shuffle(scoredPool);

  const picksExp    = orderedExp.slice(0, cap);
  const scoredLimit = totalLimit - picksExp.length;
  const picksScored = orderedScored.slice(0, scoredLimit);

  const combined = [...picksScored, ...picksExp];
  // Re-shuffle the combined list so experimental questions are not all
  // clustered at the end of the session.  Deterministic mode preserves the
  // scored-then-experimental ordering for reproducible tests.
  return deterministic ? combined : shuffle(combined);
}

function partitionByExperimental(pool) {
  const scored = [];
  const experimental = [];
  for (const q of pool) {
    if (q.isExperimental === true) experimental.push(q);
    else scored.push(q);
  }
  return { scored, experimental };
}

function getExperimentalConfig(examConfig, mode) {
  // Per-mode override wins, then per-exam fallback, then default-disabled.
  let modeKey;
  if (mode === 'full')         modeKey = 'fullExam';
  else if (mode === 'practice') modeKey = 'practiceExam';
  else if (mode === 'targeted') modeKey = 'targetedQuiz';
  else if (mode === 'targetedTest') modeKey = 'targetedTest';
  else                          modeKey = null;
  const perMode = modeKey ? examConfig?.[modeKey]?.experimental : null;
  const perExam = examConfig?.experimental;
  const cfg = perMode ?? perExam ?? null;
  if (!cfg || cfg.enabled !== true || !(cfg.count > 0)) return null;
  return cfg;
}

export function createSession(examId, mode, pool = null, options = {}) {
  const {
    deterministic = false,
    excludeIds = [],
    phases = [],
    // Targeted Test (user-configured) options.  filters is a map of
    // metadata-field-name → array of accepted values, e.g.
    //   { category: ['Planning', 'Closing'], performanceDomain: ['People'] }
    // count is the requested session length.  When the strict-filtered pool
    // has fewer matching questions than `count`, all matching questions are
    // returned — the caller is expected to surface the shortfall to the user
    // before starting (see TargetedTestConfig).  Unlike the post-results
    // `targeted` mode, no out-of-filter questions are ever mixed in.
    filters = null,
    count   = null,
  } = options;
  const config = getExamConfig(examId);
  // PR 2C: createSession requires an EXPLICIT pool (no synchronous loadQuestions
  // fallback). The only production caller (App.buildAndStartSession) always
  // passes the lazily-loaded pool; a missing pool yields an empty session.
  const rawPool = Array.isArray(pool) ? pool : [];

  // Enforce active-only: exclude 'new' and 'retired' questions from learner sessions.
  // Questions from the bundled JSON fallback all have status='active' by construction.
  // Random pools serve only standalone classic questions. Case-linked rows
  // are delivered by the case runner / exam case blocks, and interactive
  // types exist only inside cases — both are excluded as defense in depth
  // (the admin bank loader now includes them, so `status` alone is no longer
  // the only barrier between case content and the random pools).
  const POOLABLE_TYPES = new Set(['multiple_choice', 'multiple_select', 'visual_interpretation']);
  const activePool = rawPool.filter((q) =>
    (!q.status || q.status === 'active')
    && !q.caseStudyId
    && (!q.type || POOLABLE_TYPES.has(q.type)));

  // Filter out quarantined questions before shuffling/slicing so the limit
  // is applied to the available (non-quarantined) pool.
  const excludeSet = excludeIds instanceof Set ? excludeIds : new Set(excludeIds);
  const pool_ = excludeSet.size > 0
    ? activePool.filter((q) => !excludeSet.has(String(q.id)))
    : activePool;

  // Per-mode experimental config — null if disabled / count<=0.
  const expConfig = getExperimentalConfig(config, mode);
  const { scored: scoredPool, experimental: expPool } =
    expConfig ? partitionByExperimental(pool_) : { scored: pool_, experimental: [] };

  if (mode === 'full') {
    const limit = config.fullExam.totalQuestions;
    // Config-driven stratification: { field, percentages }. Most certs draw on
    // `category`; CPMAI declares fullExamDistribution on `ecoDomain` so the
    // sim follows PMI's ECO weights (see exams.js). Resolved from the config
    // object (not a separate exams.js export) so tests that mock getExamConfig
    // keep working — mirrors exams.getFullExamDistribution exactly.
    const strat = resolveFullExamStratification(config);
    const dist  = strat ? strat.percentages : null;
    const field = strat ? strat.field : 'category';

    // Experimental capture path — supersedes weighted distribution because
    // the experimental count is taken off the top of the total.  Distribution
    // applies only to the scored remainder.  When experimental is disabled
    // (expConfig === null) we fall through to the historical behaviour.
    if (expConfig) {
      const scoredLimit = Math.max(0, limit - Math.min(expConfig.count, expPool.length));
      const scoredPicks = dist
        ? selectWeighted(scoredPool, dist, scoredLimit, deterministic, field)
        : (deterministic ? scoredPool : shuffle(scoredPool)).slice(0, scoredLimit);
      const orderedExp = deterministic ? expPool : shuffle(expPool);
      const expPicks   = orderedExp.slice(0, Math.min(expConfig.count, expPool.length));
      const combined   = [...scoredPicks, ...expPicks];
      return deterministic ? combined : shuffle(combined);
    }

    if (dist) {
      return selectWeighted(pool_, dist, limit, deterministic, field);
    }
    const ordered = deterministic ? pool_ : shuffle(pool_);
    return ordered.slice(0, limit);
  }

  if (mode === 'practice') {
    const limit = config.practiceExam.defaultQuestions;
    if (expConfig) {
      return applyExperimentalSelection(scoredPool, expPool, limit, expConfig, deterministic);
    }
    const ordered = deterministic ? pool_ : shuffle(pool_);
    return ordered.slice(0, limit);
  }

  if (mode === 'targeted') {
    const limit = config.targetedQuiz?.defaultQuestions ?? 15;
    const phaseSet = new Set(phases);

    let phasePool = phaseSet.size > 0
      ? pool_.filter((q) => phaseSet.has(q.category ?? ''))
      : pool_;
    if (phasePool.length < limit) {
      const remaining = pool_.filter((q) => !phaseSet.has(q.category ?? ''));
      phasePool = [...phasePool, ...remaining];
    }

    if (expConfig) {
      const { scored: phaseScored, experimental: phaseExp } = partitionByExperimental(phasePool);
      return applyExperimentalSelection(phaseScored, phaseExp, limit, expConfig, deterministic);
    }
    const ordered = deterministic ? phasePool : shuffle(phasePool);
    return ordered.slice(0, limit);
  }

  if (mode === 'targetedTest') {
    // User-configured targeted test: strict metadata filtering, no fallback.
    // The caller (TargetedTestConfig) is responsible for ensuring at least
    // one filter is selected before invoking createSession.
    const limit = Math.max(1, count ?? config.targetedTest?.defaultCount ?? 15);
    const matched = filterByMetadata(pool_, filters);
    const ordered = deterministic ? matched : shuffle(matched);
    return ordered.slice(0, limit);
  }

  return pool_;
}

// ── Metadata helpers (Targeted Test) ──────────────────────────────────────────
//
// Both helpers are pool-driven (no exam-specific hardcoding) so they work
// uniformly across PMP, CPMAI, PMI-ACP, and any future certification.

/**
 * Returns true when `q` matches every filter group in `filters`.
 *   filters[fieldName] = [acceptedValue, …]
 *
 * - Empty arrays are ignored (treated as "no filter on that field").
 * - For scalar fields (category, performanceDomain, …) the question matches
 *   when its value is in the accepted list.
 * - For array fields (tags) the question matches when ANY of its values is in
 *   the accepted list.
 * - When `filters` is null / has no active groups, every question matches.
 */
function matchesFilters(q, filters) {
  if (!filters) return true;
  const groups = Object.entries(filters).filter(([, vs]) => Array.isArray(vs) && vs.length > 0);
  if (groups.length === 0) return true;
  for (const [field, accepted] of groups) {
    const acceptedSet = new Set(accepted);
    const value = q?.[field];
    if (Array.isArray(value)) {
      // Stringify each item so boolean / number values match the
      // string-typed option list extractMetadataOptions produces.
      if (!value.some((v) => acceptedSet.has(String(v)))) return false;
    } else if (value === null || value === undefined || value === '') {
      return false;
    } else if (!acceptedSet.has(String(value))) {
      return false;
    }
  }
  return true;
}

/**
 * Filters a question pool down to those matching the given metadata filters.
 * Exported for tests and for live "match count" displays in the config UI.
 *
 * @param {object[]} pool
 * @param {Object<string,string[]>|null} filters
 * @returns {object[]}
 */
export function filterByMetadata(pool, filters) {
  if (!Array.isArray(pool)) return [];
  return pool.filter((q) => matchesFilters(q, filters));
}

// Candidate metadata fields the Targeted Test UI may surface. A field only
// appears in the UI when at least two distinct non-empty values are present
// in the live question pool for the selected exam — the available filters
// are therefore data-driven, not hardcoded per exam.
const METADATA_FIELDS = [
  { key: 'category',          label: 'Category' },
  { key: 'performanceDomain', label: 'Performance Domain' },
  { key: 'deliveryContext',   label: 'Delivery Context' },
  { key: 'subcategory',       label: 'Sub-category' },
  { key: 'tags',              label: 'Tags' }, // array-valued
  // PMI-ACP: only Practice Area is surfaced as a learner-facing filter
  // alongside Category (4 ECO domains) and Difficulty above. The other
  // ECO metadata fields (knowledgeArea, artifact, cognitiveLevel,
  // scenarioBased, examWeight, ecoTask) are intentionally hidden from
  // the Targeted Test UI to keep learner choices simple. They remain
  // editable in Question Editor, persisted in the DB (migrations 032+033),
  // and available for analytics + exam balancing.
  { key: 'practiceArea',      label: 'Practice Area' },
  // Secondary dimension for certs that declare one (e.g. PMOCP Focus Areas).
  // Auto-surfaced as a Targeted Test filter group only when questions carry it
  // (extractMetadataOptions hides fields with < 2 distinct values).
  { key: 'focusArea',         label: 'Focus Area' },
  // PMI ECO exam domain. GATED: questions.eco_domain is populated for CPMAI
  // (five canonical domains, enforced by migration 096) AND for PMI-ACP (its
  // own vocabulary with known historical drift). Surfacing it purely on
  // "≥2 distinct values in the pool" would expose ACP's drifting values, so
  // this field appears only for a cert whose exams.js metadataDisplay.ecoDomain
  // declares a valueAllowlist. See extractMetadataOptions().
  { key: 'ecoDomain',         label: 'Exam Domain', gated: true },
];

/**
 * True when a gated metadata field may be surfaced for `examId`: the cert's
 * metadataDisplay[key] must declare a non-empty valueAllowlist. Ungated fields
 * are always eligible. No examId → gated fields stay hidden (fail closed).
 */
function isMetadataFieldEligible(field, examId) {
  if (!field.gated) return true;
  if (!examId) return false;
  const display = getExamConfig(examId)?.metadataDisplay || {};
  const allow = display[field.key]?.valueAllowlist;
  return Array.isArray(allow) && allow.length > 0;
}

/**
 * Inspects the question pool and returns the metadata filter groups that
 * should be offered in the Targeted Test config UI.
 *
 * Each group has the form:
 *   { key, label, values: string[] }
 *
 * A field is INCLUDED only when the pool contains at least two distinct
 * non-empty values for it. This means PMP exposes performanceDomain /
 * deliveryContext (when populated), while CPMAI and PMI-ACP automatically
 * surface only the fields they actually populate — no exam-specific code.
 *
 * Values inside each group are returned alphabetically for stable rendering.
 *
 * Gated fields (see METADATA_FIELDS) additionally require `examId` to declare
 * a metadataDisplay valueAllowlist for that key — omit examId and they are
 * never surfaced.
 *
 * @param {object[]} pool
 * @param {string|null} [examId]
 * @returns {Array<{ key: string, label: string, values: string[] }>}
 */
export function extractMetadataOptions(pool, examId = null) {
  if (!Array.isArray(pool) || pool.length === 0) return [];
  const out = [];
  for (const field of METADATA_FIELDS) {
    if (!isMetadataFieldEligible(field, examId)) continue;
    const { key, label } = field;
    const values = new Set();
    for (const q of pool) {
      const v = q?.[key];
      if (Array.isArray(v)) {
        for (const item of v) {
          if (item !== null && item !== undefined && item !== '') values.add(String(item));
        }
      } else if (v !== null && v !== undefined && v !== '') {
        values.add(String(v));
      }
    }
    if (values.size >= 2) {
      out.push({ key, label, values: [...values].sort((a, b) => a.localeCompare(b)) });
    }
  }
  return out;
}

// Exported for tests that want to verify experimental selection directly
// without going through createSession's mode dispatch.
export const _experimentalInternals = {
  applyExperimentalSelection,
  partitionByExperimental,
  getExperimentalConfig,
};
